import { useState, useEffect, useRef, useCallback } from 'react';
import { useTenant } from '../context/TenantContext.js';
import { tenants as tenantsApi, uploadFile } from '../api/client.js';
import { LoadingSpinner } from '../components/LoadingSpinner.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useToast } from '../components/Toast.js';
import type { BotConfig } from '../types/index.js';

const STATUS_OPTIONS = [
  { value: 'online', label: 'Online', color: '#3ba55d' },
  { value: 'idle', label: 'Idle', color: '#faa81a' },
  { value: 'dnd', label: 'Do Not Disturb', color: '#ed4245' },
  { value: 'invisible', label: 'Invisible', color: '#747f8d' },
] as const;

const ACTIVITY_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'playing', label: 'Playing' },
  { value: 'listening', label: 'Listening to' },
  { value: 'watching', label: 'Watching' },
  { value: 'competing', label: 'Competing in' },
] as const;

export function BotPersonalizerPage() {
  const { activeTenantId, activeTenant, refreshActiveTenant, loading: tenantLoading, error: tenantError, refreshTenants } = useTenant();
  const { toast } = useToast();

  // form state
  const [avatarUrl, setAvatarUrl] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [status, setStatus] = useState<BotConfig['status']>('online');
  const [activityType, setActivityType] = useState<BotConfig['activityType'] | ''>('');
  const [activityText, setActivityText] = useState('');

  // ui state
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [updatingName, setUpdatingName] = useState(false);

  const initialSyncDone = useRef(false);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const avatarFileRef = useRef<HTMLInputElement>(null);
  const bannerFileRef = useRef<HTMLInputElement>(null);

  // sync form from tenant
  useEffect(() => {
    if (!activeTenant) return;
    initialSyncDone.current = false;
    const cfg = activeTenant.botConfig || {};
    setAvatarUrl(cfg.avatarUrl ?? '');
    setBannerUrl(cfg.bannerUrl ?? '');
    setDisplayName(cfg.displayName ?? '');
    setStatus(cfg.status ?? 'online');
    setActivityType(cfg.activityType ?? '');
    setActivityText(cfg.activityText ?? '');
    requestAnimationFrame(() => {
      initialSyncDone.current = true;
    });
  }, [activeTenant]);

  // auto-save (presence/status changes only — excludes displayName & image uploads which save immediately)
  const doAutoSave = useCallback(async (overrides?: Partial<BotConfig>) => {
    if (!activeTenantId) return;
    setSaving(true);
    try {
      const config: Partial<BotConfig> = {
        avatarUrl: avatarUrl || undefined,
        bannerUrl: bannerUrl || undefined,
        status,
        activityType: (activityType || undefined) as BotConfig['activityType'],
        activityText: activityText || undefined,
        ...overrides,
      };
      await tenantsApi.update(activeTenantId, { botConfig: config });
      await refreshActiveTenant();
    } catch (err) {
      console.error('[bot] auto-save failed:', err);
      toast('Failed to save bot settings', 'error');
    } finally {
      setSaving(false);
    }
  }, [activeTenantId, avatarUrl, bannerUrl, status, activityType, activityText, refreshActiveTenant, toast]);

  // debounced auto-save on presence/status changes
  useEffect(() => {
    if (!initialSyncDone.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      void doAutoSave();
    }, 1200);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [status, activityType, activityText]); // eslint-disable-line react-hooks/exhaustive-deps

  // image upload handler
  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'avatarUrl' | 'bannerUrl',
    setLoading: (v: boolean) => void,
    setUrl: (v: string) => void,
    fileRef: React.RefObject<HTMLInputElement | null>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const url = await uploadFile(file);
      setUrl(url);
      // save immediately after upload
      await doAutoSave({ [field]: url });
      toast('Image uploaded', 'success');
    } catch (err) {
      console.error('[bot] upload failed:', err);
      toast(err instanceof Error ? err.message : 'Upload failed', 'error');
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // explicit display name update (rate-limited by Discord)
  const handleUpdateName = async () => {
    if (!activeTenantId || !displayName.trim()) return;
    setUpdatingName(true);
    try {
      await tenantsApi.update(activeTenantId, {
        botConfig: {
          ...(activeTenant?.botConfig || {}),
          displayName: displayName.trim(),
        },
      });
      await refreshActiveTenant();
      toast('Display name update sent to Discord', 'success');
    } catch (err) {
      console.error('[bot] name update failed:', err);
      toast('Failed to update display name', 'error');
    } finally {
      setUpdatingName(false);
    }
  };

  // clear image
  const handleClearImage = async (field: 'avatarUrl' | 'bannerUrl', setUrl: (v: string) => void) => {
    setUrl('');
    await doAutoSave({ [field]: '' });
  };

  if (tenantLoading) return <LoadingSpinner />;
  if (tenantError) return <ErrorMessage message={tenantError} onRetry={refreshTenants} />;
  if (!activeTenant) return <ErrorMessage message="No server selected" />;

  const currentStatus = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  const currentActivity = ACTIVITY_OPTIONS.find(a => a.value === activityType);

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Bot Personalizer</h1>
        {saving && (
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="spinner-sm" /> Saving...
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, alignItems: 'start' }}>
        {/* ── left: config form ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* profile card */}
          <div className="kpi" style={{ padding: 24 }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600 }}>Profile</h3>

            {/* avatar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <div
                onClick={() => avatarFileRef.current?.click()}
                style={{
                  width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.04)', border: '2px dashed rgba(255,255,255,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#818cf8'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
              >
                {uploadingAvatar ? (
                  <span className="spinner-sm" />
                ) : avatarUrl ? (
                  <img src={avatarUrl} alt="Bot avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M20 21a8 8 0 1 0-16 0" />
                  </svg>
                )}
              </div>
              <input
                ref={avatarFileRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={e => void handleImageUpload(e, 'avatarUrl', setUploadingAvatar, setAvatarUrl, avatarFileRef)}
                style={{ display: 'none' }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Avatar</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Click to upload. PNG, JPG, GIF, or WebP.</div>
                {avatarUrl && (
                  <button
                    onClick={() => void handleClearImage('avatarUrl', setAvatarUrl)}
                    style={{
                      marginTop: 6, padding: '3px 10px', fontSize: 11, background: 'rgba(237,66,69,0.15)',
                      color: '#ed4245', border: '1px solid rgba(237,66,69,0.3)', borderRadius: 4, cursor: 'pointer',
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            {/* banner */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Banner</div>
              <div
                onClick={() => bannerFileRef.current?.click()}
                style={{
                  width: '100%', height: 120, borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.04)', border: '2px dashed rgba(255,255,255,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#818cf8'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
              >
                {uploadingBanner ? (
                  <span className="spinner-sm" />
                ) : bannerUrl ? (
                  <img src={bannerUrl} alt="Bot banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>Click to upload banner image</span>
                )}
              </div>
              <input
                ref={bannerFileRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={e => void handleImageUpload(e, 'bannerUrl', setUploadingBanner, setBannerUrl, bannerFileRef)}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>
                Banner requires the bot to be in 10+ servers (Discord limitation).
              </div>
              {bannerUrl && (
                <button
                  onClick={() => void handleClearImage('bannerUrl', setBannerUrl)}
                  style={{
                    marginTop: 6, padding: '3px 10px', fontSize: 11, background: 'rgba(237,66,69,0.15)',
                    color: '#ed4245', border: '1px solid rgba(237,66,69,0.3)', borderRadius: 4, cursor: 'pointer',
                  }}
                >
                  Remove
                </button>
              )}
            </div>

            {/* display name */}
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Display Name</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  className="form-input"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="Bot username"
                  maxLength={32}
                  style={{ flex: 1 }}
                />
                <button
                  className="btn-sm btn-primary"
                  onClick={() => void handleUpdateName()}
                  disabled={updatingName || !displayName.trim()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {updatingName ? 'Updating...' : 'Update Name'}
                </button>
              </div>
              <div style={{
                marginTop: 8, padding: '8px 12px', borderRadius: 6,
                background: 'rgba(250,168,26,0.08)', border: '1px solid rgba(250,168,26,0.2)',
                fontSize: 12, color: '#faa81a', lineHeight: 1.5,
              }}>
                Discord limits name changes to 2 per hour. Changes apply globally across all servers the bot is in.
              </div>
            </div>
          </div>

          {/* presence card */}
          <div className="kpi" style={{ padding: 24 }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600 }}>Presence</h3>

            {/* status */}
            <div style={{ marginBottom: 20 }}>
              <label className="form-label">Online Status</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {STATUS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setStatus(opt.value)}
                    style={{
                      padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                      background: status === opt.value ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.02)',
                      border: status === opt.value ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.06)',
                      color: status === opt.value ? '#f0f0f5' : 'rgba(255,255,255,0.5)',
                      display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s',
                    }}
                  >
                    <span style={{
                      width: 10, height: 10, borderRadius: '50%', background: opt.color,
                      boxShadow: status === opt.value ? `0 0 6px ${opt.color}` : 'none',
                    }} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* activity */}
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Activity Type</label>
              <select
                className="form-input"
                value={activityType}
                onChange={e => setActivityType(e.target.value as BotConfig['activityType'] | '')}
              >
                {ACTIVITY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {activityType && (
              <div>
                <label className="form-label">Activity Text</label>
                <input
                  type="text"
                  className="form-input"
                  value={activityText}
                  onChange={e => setActivityText(e.target.value)}
                  placeholder={`e.g. ${activityType === 'listening' ? '/help' : activityType === 'watching' ? 'the server' : activityType === 'competing' ? 'crypto' : 'with payments'}`}
                  maxLength={128}
                />
              </div>
            )}
          </div>
        </div>

        {/* ── right: live preview ── */}
        <div className="kpi" style={{ padding: 0, overflow: 'hidden', position: 'sticky', top: 24 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.4)' }}>
            Preview
          </div>

          {/* banner area */}
          <div style={{
            height: 80, background: bannerUrl
              ? `url(${bannerUrl}) center/cover`
              : 'linear-gradient(135deg, #5865f2 0%, #818cf8 100%)',
          }} />

          {/* profile section */}
          <div style={{ padding: '0 16px 16px', position: 'relative' }}>
            {/* avatar */}
            <div style={{
              width: 64, height: 64, borderRadius: '50%', overflow: 'hidden',
              border: '4px solid #1e1f22', marginTop: -32, position: 'relative',
              background: '#2b2d31',
            }}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M20 21a8 8 0 1 0-16 0" />
                  </svg>
                </div>
              )}
              {/* status dot */}
              <div style={{
                position: 'absolute', bottom: 0, right: 0,
                width: 16, height: 16, borderRadius: '50%',
                background: currentStatus.color,
                border: '3px solid #1e1f22',
              }} />
            </div>

            {/* name + activity */}
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#f2f3f5', fontFamily: 'Outfit, sans-serif' }}>
                {displayName || 'Bot Name'}
              </div>
              <div style={{ fontSize: 12, color: '#b5bac1', marginTop: 2 }}>
                bot
              </div>
              {activityType && activityText && (
                <div style={{
                  marginTop: 12, padding: '8px 10px', borderRadius: 6,
                  background: 'rgba(255,255,255,0.04)', fontSize: 12, color: '#b5bac1',
                }}>
                  <span style={{ textTransform: 'capitalize' }}>
                    {currentActivity?.label || activityType}
                  </span>{' '}
                  <span style={{ color: '#f2f3f5' }}>{activityText}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
